// PATH: src/components/providers/AccessTokensCard.tsx
import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import KeyIcon from "@mui/icons-material/VpnKeyOutlined";
import { useAppConfigsApi } from "../../api";
import { buildBaseUrl } from "../../config/config";
import type { AccessToken } from "../../models/domain";
import { StatusPill, errorMessage, useConfirm, useToast } from "../ui";

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

/** L'état d'un jeton, tel que le serveur le juge à chaque appel (révoqué, expiré, actif). */
const stateOf = (t: AccessToken): { tone: "error" | "warning" | "success"; label: string } =>
  t.revoked_at
    ? { tone: "error", label: `révoqué le ${day(t.revoked_at)}` }
    : new Date(t.expires_at) <= new Date()
      ? { tone: "warning", label: "expiré" }
      : { tone: "success", label: "actif" };

/**
 * « Accès de l'atelier » : les jetons personnels avec lesquels l'équipe
 * contenu lance tc_docsourcing depuis son poste, par le broker public — sans
 * tunnel ssh ni jeton interne, et sans aucune clé de fournisseur. Un jeton
 * vaut la session de son auteur sur tc_embed_be seulement (rédaction par la
 * passerelle, imports) ; il ne règle rien et ne crée pas d'autre jeton.
 */
const AccessTokensCard: React.FC = () => {
  const { listAccessTokens, createAccessToken, revokeAccessToken } = useAppConfigsApi();
  const toast = useToast();
  const confirm = useConfirm();
  const [tokens, setTokens] = React.useState<AccessToken[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [label, setLabel] = React.useState("");
  const [days, setDays] = React.useState(90);
  const [busy, setBusy] = React.useState(false);
  const [created, setCreated] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setError(null);
    try {
      setTokens((await listAccessTokens()).data.tokens);
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger les jetons."));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const r = (await createAccessToken(label.trim(), days)).data;
      setCreating(false);
      setLabel("");
      setCreated(r.token);
      void load();
    } catch (e) {
      toast.error(errorMessage(e, "Jeton non créé."));
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (t: AccessToken) => {
    const ok = await confirm({
      title: `Révoquer « ${t.label} » ?`,
      message: "L'atelier lancé avec ce jeton est refusé dès son appel suivant. Un nouveau jeton se crée en un clic.",
      confirmLabel: "Révoquer",
      danger: true,
    });
    if (!ok) return;
    try {
      await revokeAccessToken(t.id);
      toast.success("Jeton révoqué.");
      void load();
    } catch (e) {
      toast.error(errorMessage(e, "Révocation impossible."));
    }
  };

  // Ce que la personne colle dans le `.env` de tc_docsourcing : l'adresse de
  // tc_embed_be par le broker public, et le jeton.
  const envLines = created ? `TC_EMBED_URL=${buildBaseUrl("embed")}\nTC_JETON=${created}` : "";

  return (
    <Card sx={{ mt: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ p: 2.5 }}>
        <Box>
          <Typography variant="h5">Accès de l'atelier de rédaction</Typography>
          <Typography variant="body2" color="text.secondary">
            Jetons personnels de tc_docsourcing : rédaction par la passerelle et imports, depuis le poste, sans tunnel ni clé de
            fournisseur. Chacun crée le sien ; tout administrateur peut révoquer.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<KeyIcon />} onClick={() => setCreating(true)}>
          Créer mon jeton
        </Button>
      </Stack>
      {error && (
        <Alert severity="error" sx={{ mx: 2.5, mb: 2 }}>
          {error}
        </Alert>
      )}
      {tokens && tokens.length > 0 && (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Jeton</TableCell>
                <TableCell>Propriétaire</TableCell>
                <TableCell>Créé</TableCell>
                <TableCell>Expire</TableCell>
                <TableCell>Dernier usage</TableCell>
                <TableCell>État</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tokens.map((t) => {
                const st = stateOf(t);
                return (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 650 }}>
                        {t.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        •••• {t.last4}
                      </Typography>
                    </TableCell>
                    <TableCell>{t.owner_name || `#${t.userapp_id}`}</TableCell>
                    <TableCell>{day(t.created_at)}</TableCell>
                    <TableCell>{day(t.expires_at)}</TableCell>
                    <TableCell>{day(t.last_used_at)}</TableCell>
                    <TableCell>
                      <StatusPill tone={st.tone}>{st.label}</StatusPill>
                    </TableCell>
                    <TableCell align="right">
                      {!t.revoked_at && (
                        <Button size="small" color="error" onClick={() => void revoke(t)}>
                          Révoquer
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={creating} onClose={() => setCreating(false)} fullWidth maxWidth="xs">
        <DialogTitle>Créer mon jeton d'accès</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="Libellé"
              placeholder="ex. poste de Samira"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              inputProps={{ maxLength: 80 }}
            />
            <TextField
              type="number"
              label="Durée (jours)"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              inputProps={{ min: 1, max: 365 }}
              helperText="Entre 1 et 365 jours."
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setCreating(false)}>
            Annuler
          </Button>
          <Button variant="contained" disabled={busy || !label.trim()} onClick={() => void create()}>
            Créer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(created)} onClose={() => setCreated(null)} fullWidth maxWidth="sm">
        <DialogTitle>Votre jeton</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Il ne sera plus jamais affiché. Collez ces deux lignes dans le fichier .env de tc_docsourcing, sur votre poste
            seulement ; ne l'envoyez à personne.
          </Alert>
          <TextField fullWidth multiline value={envLines} InputProps={{ readOnly: true, sx: { fontFamily: "monospace" } }} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              void navigator.clipboard?.writeText(envLines).then(() => toast.success("Copié."));
            }}
          >
            Copier
          </Button>
          <Button variant="contained" onClick={() => setCreated(null)}>
            C'est noté
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AccessTokensCard;
